/**
 * NewsletterSignupBlock Component
 *
 * Custom BlockNote React component for embedding newsletter sign-up forms
 * with multiple layout variations and full customization options.
 *
 * Features:
 * - 4 layout variations (centered, side-by-side, hero, minimal)
 * - Newsletter list integration with Brevo
 * - Configurable opt-in strategy (single vs double opt-in)
 * - Per-block welcome email control (double opt-in only)
 * - Optional first/last name field collection
 * - Customizable text content for all form elements
 * - Media browser integration for hero layouts
 * - Overlay opacity controls for hero layout
 * - Privacy notice and policy link configuration
 * - Success handling (inline message or redirect)
 * - Edit/View mode distinction
 * - Collapsible configuration UI
 *
 * Props (from block.props):
 * - layout: Layout type - centered/side-by-side/hero/minimal (default: "centered")
 * - title: Form heading text (default: "Stay Updated")
 * - description: Form description text (default: "Get our latest news delivered to your inbox")
 * - newsletterListIds: Array of list IDs for multi-list subscriptions (required)
 * - optInStrategy: Required opt-in mode - "single" or "double" (default: "" - must be selected)
 * - sendWelcomeEmail: Send welcome email after confirmation - double opt-in only (default: false)
 * - showName: Show single name field - simpler alternative to first/last name (default: false)
 * - namePlaceholder: Single name field placeholder (default: "Your name")
 * - showFirstName: Show first name field (default: false)
 * - showLastName: Show last name field (default: false)
 * - firstNamePlaceholder: First name field placeholder (default: "First name")
 * - lastNamePlaceholder: Last name field placeholder (default: "Last name")
 * - emailPlaceholder: Email field placeholder (default: "Enter your email address")
 * - ctaButtonText: Subscribe button text (default: "Subscribe")
 * - successMessage: Success message text (default: "Thanks for subscribing! Check your email to confirm.")
 * - thankYouPage: Optional redirect URL after successful subscription
 * - privacyNotice: Privacy notice text (default: "We respect your privacy. Unsubscribe at any time.")
 * - privacyLink: Privacy policy link URL (default: "/privacy")
 * - media: Media attachment for hero layout (image URL)
 * - overlayOpacity: Hero layout overlay opacity 0-100 (default: 70)
 *
 * Usage:
 *   This component is automatically rendered when a "newsletterSignup" block
 *   is present in BlockNote documents (Pages only, not Documents).
 *
 * Related Files:
 * - app/components/blocknote/blocks/newsletter_signup_component.rb (SSR component)
 * - app/javascript/controllers/newsletter_form_controller.js (form interactions)
 * - app/controllers/newsletter_subscriptions_controller.rb (form submission)
 * - app/controllers/admin/newsletter_settings_controller.rb (list management)
 */

import React, { useState, useEffect, useCallback } from "react";
import ImageUploadField from "./shared/ImageUploadField.jsx";

/**
 * Layout type configuration
 * Maps layout types to their display labels and descriptions
 */
const LAYOUT_TYPES = {
  centered: {
    label: "Centered",
    description: "Card-based design with centered content",
    supportsMedia: true,
  },
  "side-by-side": {
    label: "Side by Side",
    description: "Two-column layout with form on the right",
    supportsMedia: true,
  },
  hero: {
    label: "Full-width Hero",
    description: "Full-width background image with overlay",
    supportsMedia: true,
    requiresMedia: true,
  },
  minimal: {
    label: "Minimal",
    description: "Clean, simple stacked layout",
    supportsMedia: false,
  },
};

/**
 * Opt-in strategy configuration
 * Maps strategy types to their display labels and help text
 */
const OPT_IN_STRATEGIES = {
  single: {
    label: "Single Opt-In",
    helpText: "User is immediately added to list. Use for lead magnets.",
    defaultSuccessMessage:
      "Thanks for subscribing! You've been added to our list.",
  },
  double: {
    label: "Double Opt-In",
    helpText: "User must confirm via email. Use for standard newsletters.",
    defaultSuccessMessage:
      "Thanks for subscribing! Check your email to confirm.",
  },
};

/**
 * Newsletter list fetching hook
 * Fetches available newsletter lists from admin endpoint
 */
function useNewsletterLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNewsletterLists();
  }, []);

  const fetchNewsletterLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/newsletter/lists");

      if (!response.ok) {
        throw new Error("Failed to fetch newsletter lists");
      }

      const data = await response.json();
      setLists(data.lists || []);
    } catch (err) {
      console.error("Failed to fetch newsletter lists:", err);
      setError(err.message);
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  return { lists, loading, error, refetch: fetchNewsletterLists };
}

/**
 * Check if block configuration is valid
 * Returns validation status and any error messages
 */
function validateBlockConfig(props) {
  const errors = [];

  if (!props.optInStrategy) {
    errors.push("Opt-in strategy must be selected");
  }

  if (props.newsletterListIds.length === 0) {
    errors.push("At least one newsletter list must be selected");
  }

  if (props.layout === "hero" && !props.media) {
    errors.push("Hero layout requires a background image");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get the appropriate default success message based on opt-in strategy
 * @param {string} strategy - The opt-in strategy ("single" or "double")
 * @returns {string} The default success message for the strategy
 */
function getDefaultSuccessMessage(strategy) {
  if (strategy && OPT_IN_STRATEGIES[strategy]) {
    return OPT_IN_STRATEGIES[strategy].defaultSuccessMessage;
  }
  return "Thanks for subscribing! Check your email to confirm.";
}

/**
 * Edit Mode Component
 * Provides form controls for editing newsletter sign-up properties
 */
function NewsletterSignupEditor({ block, onUpdate }) {
  const [collapsed, setCollapsed] = useState(() => {
    const p = block.props || {};
    const hasContent = Boolean(
      (p.title && p.title.trim()) ||
        (p.description && p.description.trim()) ||
        (p.newsletterListIds && p.newsletterListIds.length > 0) ||
        p.showFirstName ||
        p.showLastName,
    );
    return hasContent;
  });

  const { lists, loading, error } = useNewsletterLists();

  const props = {
    layout: block.props?.layout || "centered",
    title: block.props?.title || "Stay Updated",
    description:
      block.props?.description || "Get our latest news delivered to your inbox",
    newsletterListIds: block.props?.newsletterListIds || [],
    optInStrategy: block.props?.optInStrategy || "",
    sendWelcomeEmail: block.props?.sendWelcomeEmail || false,
    showName: block.props?.showName || false,
    namePlaceholder: block.props?.namePlaceholder || "Your name",
    showFirstName: block.props?.showFirstName || false,
    showLastName: block.props?.showLastName || false,
    firstNamePlaceholder: block.props?.firstNamePlaceholder || "First name",
    lastNamePlaceholder: block.props?.lastNamePlaceholder || "Last name",
    emailPlaceholder:
      block.props?.emailPlaceholder || "Enter your email address",
    ctaButtonText: block.props?.ctaButtonText || "Subscribe",
    successMessage:
      block.props?.successMessage ||
      getDefaultSuccessMessage(block.props?.optInStrategy),
    thankYouPage: block.props?.thankYouPage || "",
    privacyNotice:
      block.props?.privacyNotice ||
      "We respect your privacy. Unsubscribe at any time.",
    privacyLink: block.props?.privacyLink || "/privacy",
    media: block.props?.media || "",
    overlayOpacity: block.props?.overlayOpacity ?? 70,
  };

  const selectedLayout = LAYOUT_TYPES[props.layout] || LAYOUT_TYPES.centered;
  const supportsMedia = selectedLayout.supportsMedia;

  const validation = validateBlockConfig(props);
  const hasOptInStrategyError = !props.optInStrategy;

  const handleImageUploaded = (url, variants) => {
    const imageUrl = variants?.["500px"] || url;
    onUpdate("media", imageUrl);
  };

  // Handle opt-in strategy change - update success message to appropriate default if unchanged
  const handleOptInStrategyChange = (newStrategy) => {
    onUpdate("optInStrategy", newStrategy);

    // Reset welcome email when switching away from double opt-in
    if (newStrategy !== "double") {
      onUpdate("sendWelcomeEmail", false);
    }

    // Update success message to appropriate default if it's currently the other strategy's default
    const currentMessage = props.successMessage;
    const oldDefault = getDefaultSuccessMessage(props.optInStrategy);
    const newDefault = getDefaultSuccessMessage(newStrategy);

    // If the current message matches the old default, update to new default
    if (currentMessage === oldDefault || !currentMessage) {
      onUpdate("successMessage", newDefault);
    }
  };

  return (
    <div className="newsletter-signup-editor w-full p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="space-y-4">
        {/* Layout Type Selector */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Layout Type
          </label>
          <select
            value={props.layout}
            onChange={(e) => {
              onUpdate("layout", e.target.value);
              // Clear media if switching to minimal layout
              if (e.target.value === "minimal") {
                onUpdate("media", "");
              }
            }}
            className="form-control"
          >
            {Object.entries(LAYOUT_TYPES).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label} - {config.description}
              </option>
            ))}
          </select>
        </div>

        {/* Newsletter List Selector */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Newsletter Lists (select one or more)
          </label>
          {loading && (
            <p className="text-sm text-neutral-500">Loading lists...</p>
          )}
          {error && (
            <p className="text-sm text-red-600 mb-2">
              Error loading lists: {error}
            </p>
          )}
          {!loading && !error && lists.length > 0 && (
            <div
              className="space-y-2 border border-gray-300 rounded-md p-3 bg-white"
              contentEditable={false}
              suppressContentEditableWarning
            >
              {lists.map((list) => {
                const isChecked = props.newsletterListIds.includes(
                  String(list.id),
                );
                return (
                  <label key={list.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const listIdStr = String(list.id);
                        const newIds = e.target.checked
                          ? [...props.newsletterListIds, listIdStr]
                          : props.newsletterListIds.filter(
                              (id) => id !== listIdStr,
                            );
                        onUpdate("newsletterListIds", newIds);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-neutral-700">
                      {list.name}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {!loading && lists.length === 0 && !error && (
            <p className="text-sm text-neutral-500 mt-1">
              No newsletter lists available. Configure lists in Admin Settings.
            </p>
          )}
          {props.newsletterListIds.length === 0 && (
            <p className="text-xs text-neutral-500 mt-1">
              Please select at least one list
            </p>
          )}
        </div>

        {/* Opt-In Strategy Selector - REQUIRED */}
        <div>
          <label
            htmlFor={`opt-in-strategy-${block.id}`}
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Opt-In Strategy <span className="text-red-600">*</span>
          </label>
          <select
            id={`opt-in-strategy-${block.id}`}
            value={props.optInStrategy}
            onChange={(e) => handleOptInStrategyChange(e.target.value)}
            className={`form-control ${hasOptInStrategyError ? "border-red-500 ring-1 ring-red-500" : ""}`}
            aria-required="true"
            aria-invalid={hasOptInStrategyError}
            aria-describedby={
              hasOptInStrategyError
                ? `opt-in-strategy-error-${block.id}`
                : `opt-in-strategy-help-${block.id}`
            }
          >
            <option value="">-- Select opt-in strategy --</option>
            {Object.entries(OPT_IN_STRATEGIES).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </select>
          {hasOptInStrategyError && (
            <p
              id={`opt-in-strategy-error-${block.id}`}
              className="text-xs text-red-600 mt-1"
              role="alert"
            >
              Required: Please select an opt-in strategy
            </p>
          )}
          {props.optInStrategy && OPT_IN_STRATEGIES[props.optInStrategy] && (
            <p
              id={`opt-in-strategy-help-${block.id}`}
              className="text-xs text-neutral-600 mt-1"
            >
              {OPT_IN_STRATEGIES[props.optInStrategy].helpText}
            </p>
          )}
        </div>

        {/* Send Welcome Email Checkbox - Only visible for Double Opt-In */}
        {props.optInStrategy === "double" && (
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={props.sendWelcomeEmail}
                onChange={(e) => onUpdate("sendWelcomeEmail", e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-neutral-700">
                Send Welcome Email After Confirmation
              </span>
            </label>
          </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Title
          </label>
          <input
            type="text"
            value={props.title}
            onChange={(e) => onUpdate("title", e.target.value)}
            placeholder="Stay Updated"
            maxLength={200}
            className="form-control"
          />
        </div>

        {/* Collapse/Expand Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
            aria-expanded={!collapsed}
            aria-controls={`newsletter-signup-section-${block.id}`}
          >
            <svg
              className={`w-4 h-4 mr-1 transform transition-transform ${collapsed ? "" : "rotate-90"}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M6 6l6 4-6 4V6z" clipRule="evenodd" />
            </svg>
            {collapsed ? "Show advanced options" : "Hide advanced options"}
          </button>
        </div>

        {/* Collapsible Advanced Options */}
        {!collapsed && (
          <div
            id={`newsletter-signup-section-${block.id}`}
            className="space-y-4 pt-2 border-t border-gray-200"
          >
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                value={props.description}
                onChange={(e) => onUpdate("description", e.target.value)}
                placeholder="Get our latest news delivered to your inbox"
                rows={3}
                maxLength={500}
                className="form-control"
              />
              <span className="text-xs text-neutral-500 mt-1">
                {props.description.length}/500 characters
              </span>
            </div>

            {/* Media Upload (conditionally shown based on layout) */}
            {supportsMedia && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {props.layout === "hero"
                    ? "Background Image (required for hero)"
                    : "Image (optional)"}
                </label>
                <ImageUploadField
                  currentImageUrl={props.media}
                  onImageUploaded={handleImageUploaded}
                  blockType="newsletterSignup"
                  blockId={block.id}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
                  maxSize={5 * 1024 * 1024}
                />
              </div>
            )}

            {/* Overlay Opacity (only for hero layout with media) */}
            {props.layout === "hero" && props.media && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Overlay Opacity: {props.overlayOpacity ?? 70}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={props.overlayOpacity ?? 70}
                  onChange={(e) =>
                    onUpdate("overlayOpacity", parseInt(e.target.value, 10))
                  }
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>Transparent</span>
                  <span>Dark</span>
                </div>
              </div>
            )}

            {/* Name Field Options */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Name Fields
              </label>
              <p className="text-xs text-neutral-500 mb-2">
                Choose either a single name field or separate first/last name
                fields
              </p>
              <div className="space-y-2">
                {/* Single Name Field Option */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={props.showName}
                    onChange={(e) => {
                      onUpdate("showName", e.target.checked);
                      // Clear first/last name if enabling single name
                      if (e.target.checked) {
                        onUpdate("showFirstName", false);
                        onUpdate("showLastName", false);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">
                    Show name field (single field)
                  </span>
                </label>
                {props.showName && (
                  <input
                    type="text"
                    value={props.namePlaceholder}
                    onChange={(e) =>
                      onUpdate("namePlaceholder", e.target.value)
                    }
                    placeholder="Your name"
                    maxLength={50}
                    className="form-control ml-6"
                  />
                )}

                {/* First/Last Name Fields Option - disabled if single name is selected */}
                <label
                  className={`flex items-center ${props.showName ? "opacity-50" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={props.showFirstName}
                    disabled={props.showName}
                    onChange={(e) =>
                      onUpdate("showFirstName", e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">
                    Show first name field
                  </span>
                </label>
                {props.showFirstName && !props.showName && (
                  <input
                    type="text"
                    value={props.firstNamePlaceholder}
                    onChange={(e) =>
                      onUpdate("firstNamePlaceholder", e.target.value)
                    }
                    placeholder="First name"
                    maxLength={50}
                    className="form-control ml-6"
                  />
                )}
                <label
                  className={`flex items-center ${props.showName ? "opacity-50" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={props.showLastName}
                    disabled={props.showName}
                    onChange={(e) => onUpdate("showLastName", e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">
                    Show last name field
                  </span>
                </label>
                {props.showLastName && !props.showName && (
                  <input
                    type="text"
                    value={props.lastNamePlaceholder}
                    onChange={(e) =>
                      onUpdate("lastNamePlaceholder", e.target.value)
                    }
                    placeholder="Last name"
                    maxLength={50}
                    className="form-control ml-6"
                  />
                )}
              </div>
            </div>

            {/* Email Placeholder */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Placeholder
              </label>
              <input
                type="text"
                value={props.emailPlaceholder}
                onChange={(e) => onUpdate("emailPlaceholder", e.target.value)}
                placeholder="Enter your email address"
                maxLength={100}
                className="form-control"
              />
            </div>

            {/* CTA Button Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Button Text
              </label>
              <input
                type="text"
                value={props.ctaButtonText}
                onChange={(e) => onUpdate("ctaButtonText", e.target.value)}
                placeholder="Subscribe"
                maxLength={50}
                className="form-control"
              />
            </div>

            {/* Success Handling */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Success Handling
              </label>
              <div>
                <label className="block text-xs text-neutral-600 mb-1">
                  Success Message (shown inline)
                  {props.optInStrategy && (
                    <span className="ml-1 text-neutral-500">
                      - Default for{" "}
                      {OPT_IN_STRATEGIES[props.optInStrategy]?.label}
                    </span>
                  )}
                </label>
                <textarea
                  value={props.successMessage}
                  onChange={(e) => onUpdate("successMessage", e.target.value)}
                  placeholder={getDefaultSuccessMessage(props.optInStrategy)}
                  rows={2}
                  maxLength={300}
                  className="form-control"
                />
                {props.optInStrategy && (
                  <p className="text-xs text-neutral-500 mt-1">
                    {props.optInStrategy === "single"
                      ? 'For single opt-in, users are added immediately. Consider: "You\'ve been added to our list."'
                      : 'For double opt-in, users must confirm. Consider: "Check your email to confirm."'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs text-neutral-600 mb-1">
                  Thank You Page URL (optional, redirects on success)
                </label>
                <input
                  type="url"
                  value={props.thankYouPage}
                  onChange={(e) => onUpdate("thankYouPage", e.target.value)}
                  placeholder="/thank-you"
                  className="form-control"
                />
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Privacy Notice
              </label>
              <input
                type="text"
                value={props.privacyNotice}
                onChange={(e) => onUpdate("privacyNotice", e.target.value)}
                placeholder="We respect your privacy. Unsubscribe at any time."
                maxLength={200}
                className="form-control"
              />
              <input
                type="text"
                value={props.privacyLink}
                onChange={(e) => onUpdate("privacyLink", e.target.value)}
                placeholder="/privacy"
                className="form-control"
              />
            </div>
          </div>
        )}

        {/* Preview Section */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-neutral-700 mb-2">Preview:</p>
          <NewsletterSignupViewer block={{ ...block, props }} />
        </div>
      </div>
    </div>
  );
}

/**
 * View Mode Component
 * Renders a simplified preview of the newsletter sign-up form
 * In production, the actual form is rendered via SSR ViewComponent
 * This preview includes data attributes that would be used by the form
 */
function NewsletterSignupViewer({ block }) {
  // Migrate old newsletterListId to newsletterListIds array
  const migrateListIds = () => {
    const listIds = block.props?.newsletterListIds || [];
    const legacyId = block.props?.newsletterListId;

    // If we have the old single ID but no array, migrate it
    if (legacyId && listIds.length === 0) {
      return [legacyId];
    }

    return listIds;
  };

  const props = {
    layout: block.props?.layout || "centered",
    title: block.props?.title || "Stay Updated",
    description:
      block.props?.description || "Get our latest news delivered to your inbox",
    newsletterListId: block.props?.newsletterListId || "",
    newsletterListIds: migrateListIds(),
    optInStrategy: block.props?.optInStrategy || "",
    sendWelcomeEmail: block.props?.sendWelcomeEmail || false,
    showName: block.props?.showName || false,
    namePlaceholder: block.props?.namePlaceholder || "Your name",
    showFirstName: block.props?.showFirstName || false,
    showLastName: block.props?.showLastName || false,
    emailPlaceholder:
      block.props?.emailPlaceholder || "Enter your email address",
    ctaButtonText: block.props?.ctaButtonText || "Subscribe",
    successMessage:
      block.props?.successMessage ||
      getDefaultSuccessMessage(block.props?.optInStrategy),
    media: block.props?.media || "",
    overlayOpacity: block.props?.overlayOpacity ?? 70,
  };

  // Validation check - require opt-in strategy (Task 4.1, 4.3)
  if (!props.optInStrategy) {
    return (
      <div
        className="newsletter-signup-placeholder p-8 bg-yellow-50 border border-yellow-200 rounded-lg text-center"
        role="alert"
        aria-live="polite"
      >
        <div className="flex justify-center mb-3">
          <svg
            className="w-8 h-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-yellow-800 font-medium mb-1">
          Newsletter Sign-up Block: Configuration Required
        </p>
        <p className="text-sm text-yellow-700">
          Please select an opt-in strategy (Single or Double Opt-In) to enable
          this form.
        </p>
      </div>
    );
  }

  // Validation check - require at least one list ID (Task 4.3)
  const hasListIds =
    props.newsletterListIds.length > 0 || props.newsletterListId;
  if (!hasListIds) {
    return (
      <div
        className="newsletter-signup-placeholder p-8 bg-gray-100 rounded-lg text-center text-neutral-500"
        role="alert"
        aria-live="polite"
      >
        <p>Newsletter Sign-up: Please select at least one newsletter list</p>
      </div>
    );
  }

  // Hero layout requires media (Task 4.3)
  if (props.layout === "hero" && !props.media) {
    return (
      <div
        className="newsletter-signup-placeholder p-8 bg-gray-100 rounded-lg text-center text-neutral-500"
        role="alert"
        aria-live="polite"
      >
        <p>Newsletter Sign-up (Hero): Please add a background image</p>
      </div>
    );
  }

  // Get opt-in strategy badge (Task 4.1)
  const optInBadge = () => {
    const strategyLabel =
      props.optInStrategy === "single" ? "Single Opt-In" : "Double Opt-In";
    const badgeColor =
      props.optInStrategy === "single"
        ? "bg-blue-100 text-blue-800"
        : "bg-green-100 text-green-800";

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}
        aria-label={`Subscription type: ${strategyLabel}`}
      >
        {strategyLabel}
      </span>
    );
  };

  // Get success message indicator based on strategy (Task 4.4)
  const getSuccessMessageIndicator = () => {
    const customMessage = props.successMessage;
    const defaultMessage = getDefaultSuccessMessage(props.optInStrategy);
    const isCustom = customMessage && customMessage !== defaultMessage;

    return (
      <div className="mt-2 text-xs text-neutral-500">
        <span className="font-medium">On Success:</span>{" "}
        {isCustom ? (
          <span className="italic">"{customMessage}"</span>
        ) : (
          <span>
            {props.optInStrategy === "single"
              ? "Immediate confirmation message"
              : "Check email confirmation message"}
          </span>
        )}
      </div>
    );
  };

  // Simplified preview rendering based on layout
  // Note: Hidden form fields (opt_in_strategy, send_welcome_email) are rendered
  // by the SSR ViewComponent, not this React preview. The preview shows the configuration
  // visually to help content creators understand what will be rendered.
  const renderPreview = () => {
    switch (props.layout) {
      case "hero":
        return (
          <div
            className="relative rounded-lg overflow-hidden"
            style={{ minHeight: "300px" }}
            data-opt-in-strategy={props.optInStrategy}
            data-send-welcome-email={props.sendWelcomeEmail.toString()}
          >
            {props.media && (
              <div className="absolute inset-0">
                <img
                  src={props.media}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 bg-gray-900"
                  style={{ opacity: props.overlayOpacity / 100 }}
                />
              </div>
            )}
            <div className="relative z-10 p-8 text-white">
              <div className="mb-2">{optInBadge()}</div>
              <h3 className="text-2xl font-bold mb-2">{props.title}</h3>
              <p className="mb-4">{props.description}</p>
              <div className="flex gap-2 max-w-md">
                <input
                  type="email"
                  placeholder={props.emailPlaceholder}
                  disabled
                  className="flex-1 px-4 py-2 rounded-md bg-white text-neutral-900"
                  aria-label="Email address (preview)"
                />
                <button
                  disabled
                  className="px-6 py-2 bg-red-600 text-white rounded-md font-semibold"
                  aria-label="Subscribe button (preview)"
                >
                  {props.ctaButtonText}
                </button>
              </div>
              {getSuccessMessageIndicator()}
            </div>
          </div>
        );

      case "side-by-side":
        return (
          <div
            className="bg-gray-900 rounded-lg p-6"
            data-opt-in-strategy={props.optInStrategy}
            data-send-welcome-email={props.sendWelcomeEmail.toString()}
          >
            <div className="mb-2">{optInBadge()}</div>
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div className="text-white">
                <h3 className="text-xl font-bold mb-2">{props.title}</h3>
                <p className="text-neutral-300">{props.description}</p>
              </div>
              <div className="space-y-2">
                {props.showName && (
                  <input
                    type="text"
                    placeholder={props.namePlaceholder}
                    disabled
                    className="w-full px-4 py-2 rounded-md"
                    aria-label="Name (preview)"
                  />
                )}
                {props.showFirstName && (
                  <input
                    type="text"
                    placeholder="First name"
                    disabled
                    className="w-full px-4 py-2 rounded-md"
                    aria-label="First name (preview)"
                  />
                )}
                {props.showLastName && (
                  <input
                    type="text"
                    placeholder="Last name"
                    disabled
                    className="w-full px-4 py-2 rounded-md"
                    aria-label="Last name (preview)"
                  />
                )}
                <input
                  type="email"
                  placeholder={props.emailPlaceholder}
                  disabled
                  className="w-full px-4 py-2 rounded-md"
                  aria-label="Email address (preview)"
                />
                <button
                  disabled
                  className="w-full px-6 py-2 bg-red-600 text-white rounded-md font-semibold"
                  aria-label="Subscribe button (preview)"
                >
                  {props.ctaButtonText}
                </button>
              </div>
            </div>
            {getSuccessMessageIndicator()}
          </div>
        );

      case "minimal":
        return (
          <div
            className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto"
            data-opt-in-strategy={props.optInStrategy}
            data-send-welcome-email={props.sendWelcomeEmail.toString()}
          >
            <div className="mb-2">{optInBadge()}</div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              {props.title}
            </h3>
            <p className="text-sm text-neutral-600 mb-4">{props.description}</p>
            <div className="space-y-2">
              {props.showName && (
                <input
                  type="text"
                  placeholder={props.namePlaceholder}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  aria-label="Name (preview)"
                />
              )}
              {props.showFirstName && (
                <input
                  type="text"
                  placeholder="First name"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  aria-label="First name (preview)"
                />
              )}
              {props.showLastName && (
                <input
                  type="text"
                  placeholder="Last name"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  aria-label="Last name (preview)"
                />
              )}
              <input
                type="email"
                placeholder={props.emailPlaceholder}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                aria-label="Email address (preview)"
              />
              <button
                disabled
                className="w-full px-4 py-2 bg-red-600 text-white rounded-md font-semibold"
                aria-label="Subscribe button (preview)"
              >
                {props.ctaButtonText}
              </button>
            </div>
            {getSuccessMessageIndicator()}
          </div>
        );

      case "centered":
      default:
        return (
          <div
            className="bg-gray-900 rounded-2xl p-8 max-w-2xl mx-auto"
            data-opt-in-strategy={props.optInStrategy}
            data-send-welcome-email={props.sendWelcomeEmail.toString()}
          >
            {props.media && (
              <img
                src={props.media}
                alt=""
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
            <div className="text-center text-white">
              <div className="mb-2">{optInBadge()}</div>
              <h3 className="text-2xl font-bold mb-2">{props.title}</h3>
              <p className="mb-6">{props.description}</p>
              <div className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto">
                {props.showName && (
                  <input
                    type="text"
                    placeholder={props.namePlaceholder}
                    disabled
                    className="px-4 py-2 rounded-md text-neutral-900"
                    aria-label="Name (preview)"
                  />
                )}
                {props.showFirstName && (
                  <input
                    type="text"
                    placeholder="First name"
                    disabled
                    className="px-4 py-2 rounded-md text-neutral-900"
                    aria-label="First name (preview)"
                  />
                )}
                {props.showLastName && (
                  <input
                    type="text"
                    placeholder="Last name"
                    disabled
                    className="px-4 py-2 rounded-md text-neutral-900"
                    aria-label="Last name (preview)"
                  />
                )}
                <input
                  type="email"
                  placeholder={props.emailPlaceholder}
                  disabled
                  className="flex-1 px-4 py-2 rounded-md text-neutral-900"
                  aria-label="Email address (preview)"
                />
                <button
                  disabled
                  className="px-6 py-2 bg-red-600 text-white rounded-md font-semibold whitespace-nowrap"
                  aria-label="Subscribe button (preview)"
                >
                  {props.ctaButtonText}
                </button>
              </div>
              {getSuccessMessageIndicator()}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="newsletter-signup-preview my-4">{renderPreview()}</div>
  );
}

/**
 * Main NewsletterSignupBlock Component
 *
 * Manages edit/view mode switching and prop updates
 */
export default function NewsletterSignupBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(
    () => editor?.isEditable ?? false,
  );

  // Detect edit/view mode
  useEffect(() => {
    setIsEditMode(editor?.isEditable ?? false);
  }, [editor]);

  // Update block property
  const handleUpdate = useCallback(
    (key, value) => {
      if (!editor) {
        console.error("No editor available");
        return;
      }

      // Use editor.getBlock to get the current block state
      const currentBlock = editor.getBlock(block.id);
      if (!currentBlock) {
        console.error("Block not found:", block.id);
        return;
      }

      const newProps = { ...currentBlock.props, [key]: value };

      editor.updateBlock(currentBlock, {
        props: newProps,
      });
    },
    [editor, block.id],
  );

  // Initialize default props if needed
  useEffect(() => {
    if (!block.props) {
      editor?.updateBlock(block, {
        props: {
          layout: "centered",
          title: "Stay Updated",
          description: "Get our latest news delivered to your inbox",
          newsletterListId: "",
          newsletterListIds: [],
          optInStrategy: "",
          sendWelcomeEmail: false,
          showName: false,
          namePlaceholder: "Your name",
          showFirstName: false,
          showLastName: false,
          firstNamePlaceholder: "First name",
          lastNamePlaceholder: "Last name",
          emailPlaceholder: "Enter your email address",
          ctaButtonText: "Subscribe",
          successMessage: "",
          thankYouPage: "",
          privacyNotice: "We respect your privacy. Unsubscribe at any time.",
          privacyLink: "/privacy",
          media: "",
          overlayOpacity: 70,
        },
      });
    }
  }, [block, editor]);

  if (isEditMode) {
    return <NewsletterSignupEditor block={block} onUpdate={handleUpdate} />;
  }

  return <NewsletterSignupViewer block={block} />;
}
